import to from 'await-to-js';
import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
    Box,
    Chip,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import type { PageConfig } from 'c-config';
import { PostAuthRoutes } from 'c-routes';
import { useCommonTranslation } from 'c-translation';
import { PermissionName } from 'c-sdk';
import type { UsageOrganisation, UsageOrganisationType } from 'c-sdk';
import { usageIcon } from 'c-main/icons';
import { useAPIClientRequest, useDateUtils } from 'c-hooks';
import ApiClient from 'c-data/apiClient';
import { LoadingSpinner } from 'c-components';
import MuiRouterLink from 'c-components/MuiRouterLink';
import { NetworkRequestState } from 'c-data-layer';
import {
    KpiGrid,
    KpiTile,
    PageStack,
    PanelRow,
    PeriodSelect,
    ShareBar,
    TimeAgo,
    UsageEmpty,
    UsageHeader,
    UsageLoadError,
    UsagePanel,
} from './UsageParts';
import { DEFAULT_PERIOD_DAYS, percentOf, periodFor, useFormatDuration, userLink } from './usageLib';
import { areaFor } from './usageAreas';

const ENGAGEMENT = [
    { key: 'power', color: '#174A94' },
    { key: 'regular', color: '#4F86D6' },
    { key: 'occasional', color: '#A9C4EC' },
    { key: 'dormant', color: '#F2A65A' },
] as const;

const comparisonColor = {
    above: 'success.main',
    typical: 'text.secondary',
    below: 'error.main',
} as const;

const Engagement = ({ engagement }: { engagement: UsageOrganisation['engagement'] }) => {
    const t = useCommonTranslation();
    const total = ENGAGEMENT.reduce((sum, band) => sum + engagement[band.key], 0);
    const summary = ENGAGEMENT.map(band =>
        t(`Modules.Admin.Usage.engagement.${band.key}`, { count: engagement[band.key] }),
    ).join(', ');

    return (
        <>
            <Box
                role="img"
                aria-label={summary}
                sx={{
                    display: 'flex',
                    height: 14,
                    borderRadius: 999,
                    overflow: 'hidden',
                    bgcolor: 'action.hover',
                }}
            >
                {ENGAGEMENT.map(band => (
                    <Box
                        key={band.key}
                        sx={{
                            width: `${percentOf(engagement[band.key], total)}%`,
                            bgcolor: band.color,
                        }}
                    />
                ))}
            </Box>
            <Box
                sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 1.5 }}
            >
                {ENGAGEMENT.map(band => (
                    <Box key={band.key} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                        <Box
                            aria-hidden="true"
                            sx={{
                                width: 10,
                                height: 10,
                                mt: 0.5,
                                borderRadius: '2px',
                                bgcolor: band.color,
                            }}
                        />
                        <Box>
                            <Typography variant="body2" fontWeight={600}>
                                {t(`Modules.Admin.Usage.engagement.${band.key}`, {
                                    count: engagement[band.key],
                                })}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                {t(`Modules.Admin.Usage.engagement.${band.key}Hint`)}
                            </Typography>
                        </Box>
                    </Box>
                ))}
            </Box>
        </>
    );
};

const UsageOrganisationPage = () => {
    const t = useCommonTranslation();
    const formatDuration = useFormatDuration();
    const { formatDateString } = useDateUtils();
    const params = useParams<{ type: UsageOrganisationType; id: string }>();
    const [days, setDays] = useState(DEFAULT_PERIOD_DAYS);
    const period = useMemo(() => periodFor(days), [days]);
    const { start, data, requestState } = useAPIClientRequest(ApiClient.Usage.organisation);

    useEffect(() => {
        to(start(params.type, Number(params.id), period));
    }, [params.type, params.id, period, start]);

    const org = data?.data?.data;
    const maxPageTime = Math.max(1, ...(org?.pages ?? []).map(page => page.time_ms));
    const typeAverage = org?.type_average_active_share;

    return (
        <PageStack>
            <UsageHeader
                title={org?.name ?? t('Modules.Admin.Usage.organisationTitle')}
                subtitle={org ? t(`Modules.Admin.Usage.types.${org.type}`) : undefined}
                controls={<PeriodSelect value={days} onChange={setDays} />}
            />

            {requestState === NetworkRequestState.Error && <UsageLoadError />}
            {requestState === NetworkRequestState.InProgress && !org && <LoadingSpinner />}

            {org && (
                <>
                    <KpiGrid>
                        <KpiTile
                            label={t('Modules.Admin.Usage.kpi.activeUsers')}
                            value={t('Modules.Admin.Usage.kpi.activeOfTotal', {
                                active: org.totals.active_users,
                                total: org.total_users,
                            })}
                            note={
                                typeAverage == null
                                    ? undefined
                                    : t('Modules.Admin.Usage.kpi.typeAverage', {
                                          share: `${Math.round(typeAverage * 100)}%`,
                                      })
                            }
                        />
                        <KpiTile
                            label={t('Modules.Admin.Usage.kpi.sessions')}
                            value={org.totals.sessions}
                            note={
                                org.totals.active_users > 0
                                    ? t('Modules.Admin.Usage.kpi.perActiveUser', {
                                          count: Math.round(
                                              org.totals.sessions / org.totals.active_users,
                                          ),
                                      })
                                    : undefined
                            }
                        />
                        <KpiTile
                            label={t('Modules.Admin.Usage.kpi.timeInPortal')}
                            value={formatDuration(org.totals.time_ms)}
                            note={
                                org.totals.sessions > 0
                                    ? t('Modules.Admin.Usage.kpi.perSession', {
                                          time: formatDuration(
                                              org.totals.time_ms / org.totals.sessions,
                                          ),
                                      })
                                    : undefined
                            }
                        />
                        <KpiTile
                            label={t('Modules.Admin.Usage.kpi.campaignsViewed')}
                            value={org.totals.campaigns_viewed}
                        />
                    </KpiGrid>

                    <PanelRow>
                        <UsagePanel title={t('Modules.Admin.Usage.whatTheyUse')}>
                            {org.pages.length === 0 ? (
                                <UsageEmpty />
                            ) : (
                                <TableContainer>
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>
                                                    {t('Modules.Admin.Usage.columns.page')}
                                                </TableCell>
                                                <TableCell>
                                                    {t('Modules.Admin.Usage.columns.users')}
                                                </TableCell>
                                                <TableCell>
                                                    {t('Modules.Admin.Usage.columns.visits')}
                                                </TableCell>
                                                <TableCell>
                                                    {t('Modules.Admin.Usage.columns.time')}
                                                </TableCell>
                                                <TableCell>
                                                    {t('Modules.Admin.Usage.columns.vsType')}
                                                </TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {org.pages.map(page => (
                                                <TableRow key={page.name}>
                                                    <TableCell>
                                                        <Typography
                                                            variant="body2"
                                                            fontWeight={500}
                                                        >
                                                            {t(
                                                                `Modules.Admin.Usage.areas.${areaFor(
                                                                    page.name,
                                                                )}`,
                                                            )}
                                                        </Typography>
                                                        <Typography
                                                            variant="caption"
                                                            color="text.secondary"
                                                        >
                                                            {page.name}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell>{page.users}</TableCell>
                                                    <TableCell>{page.visits}</TableCell>
                                                    <TableCell>
                                                        <Box
                                                            sx={{
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                gap: 1,
                                                            }}
                                                        >
                                                            <span>
                                                                {formatDuration(page.time_ms)}
                                                            </span>
                                                            <ShareBar
                                                                percent={percentOf(
                                                                    page.time_ms,
                                                                    maxPageTime,
                                                                )}
                                                                label={page.name}
                                                            />
                                                        </Box>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Typography
                                                            variant="body2"
                                                            fontWeight={600}
                                                            color={comparisonColor[page.comparison]}
                                                        >
                                                            {t(
                                                                `Modules.Admin.Usage.comparison.${page.comparison}`,
                                                            )}
                                                        </Typography>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            )}
                        </UsagePanel>
                        <UsagePanel title={t('Modules.Admin.Usage.engagementTitle')}>
                            <Engagement engagement={org.engagement} />
                        </UsagePanel>
                    </PanelRow>

                    <UsagePanel title={t('Modules.Admin.Usage.users')}>
                        {org.users.length === 0 ? (
                            <UsageEmpty />
                        ) : (
                            <TableContainer>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>
                                                {t('Modules.Admin.Usage.columns.user')}
                                            </TableCell>
                                            <TableCell>
                                                {t('Modules.Admin.Usage.columns.activeDays')}
                                            </TableCell>
                                            <TableCell>
                                                {t('Modules.Admin.Usage.columns.sessions')}
                                            </TableCell>
                                            <TableCell>
                                                {t('Modules.Admin.Usage.columns.time')}
                                            </TableCell>
                                            <TableCell>
                                                {t('Modules.Admin.Usage.columns.mostUsed')}
                                            </TableCell>
                                            <TableCell>
                                                {t('Modules.Admin.Usage.columns.actions')}
                                            </TableCell>
                                            <TableCell>
                                                {t('Modules.Admin.Usage.columns.lastSeen')}
                                            </TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {org.users.map(user => (
                                            <TableRow key={user.id}>
                                                <TableCell>
                                                    <MuiRouterLink
                                                        to={userLink(user.id)}
                                                        fontWeight={600}
                                                    >
                                                        {user.name}
                                                    </MuiRouterLink>
                                                    <Typography
                                                        variant="caption"
                                                        color="text.secondary"
                                                        display="block"
                                                    >
                                                        {user.email}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    {user.active_days === 0 ? (
                                                        <Chip
                                                            size="small"
                                                            color="warning"
                                                            label={t(
                                                                'Modules.Admin.Usage.dormantChip',
                                                            )}
                                                        />
                                                    ) : (
                                                        user.active_days
                                                    )}
                                                </TableCell>
                                                <TableCell>{user.sessions}</TableCell>
                                                <TableCell>
                                                    {formatDuration(user.time_ms)}
                                                </TableCell>
                                                <TableCell>
                                                    {user.top_page
                                                        ? t(
                                                              `Modules.Admin.Usage.areas.${areaFor(
                                                                  user.top_page,
                                                              )}`,
                                                          )
                                                        : t('Modules.Admin.Usage.duration.none')}
                                                </TableCell>
                                                <TableCell>{user.events}</TableCell>
                                                <TableCell>
                                                    {user.last_seen ? (
                                                        <TimeAgo date={user.last_seen} />
                                                    ) : (
                                                        t('Modules.Admin.Usage.never')
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}
                    </UsagePanel>
                </>
            )}
        </PageStack>
    );
};

const setup: PageConfig = {
    id: 'UsageOrganisationPage',
    component: UsageOrganisationPage,
    systemSearch: {
        title: 'Modules.Admin.Usage.organisationTitle',
        description: 'Modules.Admin.Usage.description',
        type: 'page',
        saveAsRecentPage: false,
        aliases: undefined,
        icon: usageIcon,
        route: PostAuthRoutes.Admin.UsageOrganisation,
        breadcrumbs: [
            { label: 'Modules.Admin.Home.metaTitle', link: PostAuthRoutes.Admin.AdminHome },
            { label: 'Modules.Admin.Usage.metaTitle', link: PostAuthRoutes.Admin.UsageOverview },
            { label: 'Modules.Admin.Usage.organisationTitle' },
        ],
        permissions: [PermissionName.AdminCan_view_usage],
    },
};

export default setup;
